import React from 'react'
import Button from '@material-ui/core/Button'
// @src imports
import { Button as ChromeButton } from 'src/chrome'
import { AccountFragment } from 'src/graphql'
import { AddressForm } from 'src/contacts/components'
import { useState, useUpdateAccount } from 'src/hooks'

import styles from './shippingAddress.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'

interface Props {
  account: Pick<AccountFragment, 'firstName' | 'lastName' | 'shippingAddress'>
  title: string
  isEditable?: boolean
  titleClassName?: string
}

const defaultAddress = {
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  company: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'United States',
}

const ShippingAddress: React.FC<Props> = props => {
  const { account, isEditable, title, titleClassName } = props

  const updateAccount = useUpdateAccount()

  const [isAddressVisible, setIsAddressVisible] = useState(
    !account.shippingAddress || !account.shippingAddress.address1,
  )

  const [isSavingAddress, setIsSavingAddress] = useState(false)

  const [shippingAddress, setShippingAddress] = useState(
    account.shippingAddress || defaultAddress,
  )

  const changeAddress = ({
    currentTarget: { name, value },
  }: React.FormEvent<HTMLInputElement>) => setAddress(name, value)

  const changeCountry = (value: string) => setAddress('country', value)
  const changeState = (value: string) => setAddress('state', value)

  const setAddress = (name: string, value: string) => {
    setShippingAddress({
      ...shippingAddress,
      [name]: value,
    })
  }

  const handleShowAddress = () => setIsAddressVisible(a => !a)

  const submitAddress = async () => {
    const user = {
      firstName: account.firstName || shippingAddress.firstName,
      lastName: account.lastName || shippingAddress.lastName,
      shippingAddress: shippingAddress,
    }

    setIsSavingAddress(true)
    try {
      await updateAccount(user)
      setIsAddressVisible(false)
    } finally {
      setIsSavingAddress(false)
    }
  }

  return (
    <div className={styles.componentContainer}>
      {title && (
        <div
          className={
            titleClassName
              ? `${styles.sectionTitle} ${titleClassName}`
              : styles.sectionTitle
          }
        >
          <Text type="body">{title}</Text>
          {isEditable && (
            <Button
              onClick={handleShowAddress}
              style={{
                marginLeft: 'auto',
                width: 105,
                padding: 0,
                height: '26px',
                lineHeight: '20px',
              }}
              classes={{
                label: styles.buttonLabel,
              }}
            >
              {isAddressVisible ? 'Cancel' : 'Edit Address'}
            </Button>
          )}
        </div>
      )}
      {!title && (
        <ChromeButton
          title={isAddressVisible ? 'Cancel' : 'Edit Address'}
          buttonColor={isAddressVisible ? undefined : 'pink'}
          onClick={handleShowAddress}
          style={{ marginLeft: 'auto', width: 126 }}
        />
      )}
      {isAddressVisible ? (
        <AddressForm
          contact={shippingAddress}
          onChange={changeAddress}
          onCountryChange={changeCountry}
          onStateChange={changeState}
          shouldShowDates={false}
          isTitleVisible={false}
          isSaving={isSavingAddress}
          onSubmit={submitAddress}
          style={{
            paddingRight: 30,
          }}
        />
      ) : shippingAddress.address1 ? (
        <div>
          <Text type="caption">{`${shippingAddress.firstName} ${shippingAddress.lastName}`}</Text>
          {shippingAddress.company ? (
            <Text type="caption">{`${shippingAddress.company}`}</Text>
          ) : null}
          {shippingAddress.address1 ? (
            <Text type="caption">{`${shippingAddress.address1}`}</Text>
          ) : null}
          {shippingAddress.address2 ? (
            <Text type="caption">{`${shippingAddress.address2}`}</Text>
          ) : null}
          <Text type="caption">{`${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.postalCode}`}</Text>
          {shippingAddress.country ? (
            <Text type="caption">{`${shippingAddress.country}`}</Text>
          ) : null}
        </div>
      ) : (
        <Text type="caption">No return address set</Text>
      )}
    </div>
  )
}

export default ShippingAddress
